import React, { useEffect, useState } from "react";
import { BarChart, Bar, LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import client from "@/lib/apiClient";
import { AiInsights } from "@/components/AiInsights";
import { useLang } from "@/i18n";
import { useProperty } from "@/context/PropertyContext";
import { SectionHeader, StatCard, Loader, CHART_COLORS, fmtSAR } from "@/components/shared";

export default function Energy() {
  const { t } = useLang();
  const { selectedId } = useProperty();
  const [series, setSeries] = useState(null);

  useEffect(() => {
    if (!selectedId) return;
    setSeries(null);
    client.get(`/properties/${selectedId}/energy`).then((r) => setSeries(r.data.series || []));
  }, [selectedId]);

  if (!series) return <Loader />;
  const totalBase = series.reduce((s, d) => s + d.baseline_kwh, 0);
  const totalAct = series.reduce((s, d) => s + d.actual_kwh, 0);
  const saved = totalBase - totalAct;
  const avgPct = Math.round((saved / totalBase) * 100);

  return (
    <div>
      <SectionHeader icon="zap" title={t("energy")} subtitle={t("energyIntro")} />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-5">
        <StatCard icon="trending-down" label={t("savingsPct")} value={`${avgPct}%`} accent={CHART_COLORS.emerald} testid="energy-savings" />
        <StatCard icon="zap" label={`kWh ${t("savedThisMonth")}`} value={fmtSAR(saved)} accent={CHART_COLORS.gold} />
        <StatCard icon="wallet" label={t("savedThisMonth")} value={`${fmtSAR(saved * 0.32)} ${t("sar")}`} accent={CHART_COLORS.blue} />
      </div>

      <div className="md-card p-5 mb-5">
        <h3 className="font-semibold text-foreground mb-4">{t("baseline")} vs {t("actual")} (kWh)</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={series}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--md-soft))" />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#64748B" }} tickFormatter={(d) => d.slice(5)} interval={4} />
            <YAxis tick={{ fontSize: 10, fill: "#64748B" }} />
            <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, color: "hsl(var(--foreground))" }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="baseline_kwh" fill="#475569" name={t("baseline")} radius={[3, 3, 0, 0]} />
            <Bar dataKey="actual_kwh" fill="#1E5EFF" name={t("actual")} radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="md-card p-5">
        <h3 className="font-semibold text-foreground mb-4">{t("savingsPct")} (%)</h3>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={series}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--md-soft))" />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#64748B" }} tickFormatter={(d) => d.slice(5)} interval={4} />
            <YAxis tick={{ fontSize: 10, fill: "#64748B" }} domain={[0, 40]} />
            <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, color: "hsl(var(--foreground))" }} />
            <Line type="monotone" dataKey="savings_pct" stroke="#10B981" strokeWidth={2.5} dot={false} name={t("savingsPct")} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-5"><AiInsights filter={["energy"]} /></div>
    </div>
  );
}
